
import Vue, { PropType } from 'vue';
import ShoppingCartArrowDown16 from '@carbon/icons-vue/es/shopping--cart--arrow-down/16';
import { CvButton, CvTooltip } from '@carbon/vue';
import TotalPricing from './TotalPricing.vue';
import {
  PdpChoicesSetterType,
  PriceDetailsSetterType,
} from '@/setters/typings';
import ChevronUp16 from '@carbon/icons-vue/es/chevron--up/16';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { mapState, mapActions } from 'pinia';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import {
  trySavePersonalization,
  triggerSmallDetailsCheck,
} from '@/utils/personalizationHelperV2';
import { Personalization } from '@midocean/webapps-shared-domain';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { getIntershopRedirectionUrl } from '@/utils/index';

declare module 'vue/types/vue' {
  interface Vue {
    $pdpChoices: PdpChoicesSetterType;
    $sapCustomerId: string;
    $isEditMode: boolean;
    $personalization: Personalization;
    $isDirectApproval: boolean;
    $customerCompanyLogoUrl: string;
    $isOrderDisabled: boolean;
    $context: string;
    $userLoginOptions: { data: string; iv: string };
    $isCommunicationSentToCC: boolean;
  }
}

export default Vue.extend({
  name: 'OrderDetails',
  inject: [
    '$pdpChoices',
    '$sapCustomerId',
    '$isEditMode',
    '$personalization',
    '$isDirectApproval',
    '$customerCompanyLogoUrl',
    '$isOrderDisabled',
    '$context',
    '$userLoginOptions',
    '$isCommunicationSentToCC',
  ],
  components: {
    CvButton,
    TotalPricing,
    ChevronUp16,
    CvTooltip,
  },
  props: {
    priceDetails: {
      type: Object as PropType<PriceDetailsSetterType>,
      required: true,
    },
    pdpChoicesData: {
      type: Object as PropType<PdpChoicesSetterType>,
      required: true,
    },
  },

  data() {
    return {
      ShoppingCartArrowDown16,
      showTotalPricing: false,
      getIntershopRedirectionUrl,
    };
  },

  computed: {
    ...mapState(useGlobalStore, ['isProcessingOrder', 'isLoaderActive']),
    ...mapState(useShapeStore, ['shapes', 'activeShapeId']),
    ...mapState(useProductStore, ['isAnyPositionAdded']),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useNotificationStore, [
      'redAndOrangeNotifications',
      'hasRedNotifications',
    ]),
    hasErrorsBesideSmallDetails(): boolean {
      return (
        this.hasRedNotifications &&
        this.redAndOrangeNotifications.some(
          (error) =>
            error.validatorName !== 'smallDetails' &&
            error.validatorColor === 'Red'
        )
      );
    },
    hasEmptyTexts(): boolean {
      return this.shapes.some(
        (shape) => shape.kind === 'Text' && shape.w2pText.text === ''
      );
    },
    totalQuantity(): number {
      return this.pdpChoicesData.productVariants
        .map((v) => v.quantity)
        .reduce((prev, current) => prev + current);
    },
  },
  methods: {
    ...mapActions(useGlobalStore, [
      'setIsProcessingOrder',
      'setIsLoaderActive',
    ]),

    async handleOrderItem() {
      if (DoSmallDetailsPart && triggerSmallDetailsCheck()) {
        this.$emit('action:trigger-small-details');
      } else {
        try {
          this.setIsProcessingOrder(true);
          this.setIsLoaderActive(true);
          const personalizationId = await trySavePersonalization(
            this.$isEditMode,
            this.pdpChoicesData.productVariants,
            this.$sapCustomerId,
            false,
            this.$isDirectApproval,
            this.$customerCompanyLogoUrl,
            this.languageInUse,
            this.$isCommunicationSentToCC
          );

          if (this.$context === 'CustomerCare') {
            const url = this.getIntershopRedirectionUrl(
              personalizationId,
              false,
              this.$userLoginOptions.data,
              this.$userLoginOptions.iv
            );
            window.open(url, '_blank');
          }

          this.$emit('wizard:ordered', { personalizationId });
          this.$emit('action:disable-pintura');
        } catch (e: unknown) {
          console.log((e as Error).message);
        } finally {
          this.setIsProcessingOrder(false);
        }
      }
    },
  },
});
